// El estado de TODAS las conciliaciones de todas las empresas y tarjetas, sin
// abrirlas una por una. Es la pestaña CONTROL del original.
//
// Lo que el operador busca acá es qué le falta: las tarjetas sin conciliación
// cargada, las que tienen partidas sin resolver y las que no cuadran contra el
// PDF. Los filtros están puestos para eso.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

interface Fila {
  empresa: string; tarjeta: string; cuenta: string;
  vacia: boolean; ultima: boolean;
  conciliacionId?: string; periodo?: string; cierre?: string | null;
  cerrada?: boolean; cerradaPor?: string | null;
  consumos?: number; conciliados?: number;
  nSinConciliar?: number; sumaSinConciliar?: number; sumaDiferencias?: number;
  cuadra?: boolean; saldoResumen?: number; totalCalculado?: number;
}

const pesos = (v: number | undefined) =>
  v == null ? '' : v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function PanelControl() {
  const [soloUltima, setSoloUltima] = useState(true);
  const [soloProblemas, setSoloProblemas] = useState(false);

  const q = useQuery({ queryKey: ['control'], queryFn: () => api.get<Fila[]>('/api/control') });

  const todas = q.data ?? [];
  const tieneProblema = (f: Fila) =>
    f.vacia || !!f.nSinConciliar || f.cuadra === false || (!f.cerrada && !f.vacia);

  const filas = todas
    .filter((f) => !soloUltima || f.ultima)
    .filter((f) => !soloProblemas || tieneProblema(f));

  const cuenta = {
    tarjetas: new Set(todas.map((f) => `${f.empresa}|${f.cuenta}`)).size,
    sinCargar: todas.filter((f) => f.vacia).length,
    abiertas: todas.filter((f) => !f.vacia && !f.cerrada && f.ultima).length,
    sinResolver: todas.filter((f) => f.ultima && !!f.nSinConciliar).length,
    noCuadran: todas.filter((f) => f.ultima && f.cuadra === false).length,
  };

  return (
    <>
      <h2 className="seccion">
        <span className="num">◧</span>PANEL DE CONTROL
        <span className="muted">— todas las empresas y tarjetas de un vistazo</span>
      </h2>

      <div className="cards">
        <div className="card"><div className="n">{cuenta.tarjetas}</div><div className="l">Tarjetas en el catálogo</div></div>
        <div className="card"><div className="n" style={{ color: cuenta.sinCargar ? 'var(--bad)' : 'var(--ok)' }}>{cuenta.sinCargar}</div><div className="l">Sin conciliación cargada</div></div>
        <div className="card"><div className="n" style={{ color: cuenta.abiertas ? 'var(--warn)' : 'var(--ok)' }}>{cuenta.abiertas}</div><div className="l">Abiertas, sin firmar</div></div>
        <div className="card"><div className="n" style={{ color: cuenta.sinResolver ? 'var(--warn)' : 'var(--ok)' }}>{cuenta.sinResolver}</div><div className="l">Con partidas sin resolver</div></div>
        <div className="card"><div className="n" style={{ color: cuenta.noCuadran ? 'var(--bad)' : 'var(--ok)' }}>{cuenta.noCuadran}</div><div className="l">Que no cuadran contra el PDF</div></div>
      </div>

      <div className="controls" style={{ padding: '10px 14px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, textTransform: 'none', fontSize: 13, margin: 0 }}>
          <input type="checkbox" checked={soloUltima} onChange={(e) => setSoloUltima(e.target.checked)} />
          Solo el último período de cada tarjeta
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, textTransform: 'none', fontSize: 13, margin: 0 }}>
          <input type="checkbox" checked={soloProblemas} onChange={(e) => setSoloProblemas(e.target.checked)} />
          Solo lo que necesita atención
        </label>
        <div className="spacer" />
        <span className="muted">{filas.length} de {todas.length} filas</span>
      </div>

      <div className="scroll" style={{ maxHeight: 620 }}>
        <table>
          <thead>
            <tr>
              <th>Empresa</th><th>Tarjeta</th><th>Cuenta</th><th>Período</th>
              <th className="num">Consumos</th><th className="num">Sin resolver</th>
              <th className="num">A contabilizar</th><th>Cuadre</th><th>Estado</th><th></th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={10} className="muted" style={{ padding: 14 }}>Cargando…</td></tr>}
            {!q.isLoading && filas.length === 0 && (
              <tr><td colSpan={10} className="muted" style={{ padding: 14 }}>
                {soloProblemas ? 'Nada que necesite atención. ✓' : 'No hay tarjetas en el catálogo.'}
              </td></tr>
            )}
            {filas.map((f, i) => (
              <tr key={`${f.empresa}|${f.cuenta}|${f.periodo ?? 'vacia'}|${i}`}>
                <td>{f.empresa}</td>
                <td>{f.tarjeta}</td>
                <td className="cn">{f.cuenta}</td>
                <td className="fecha">{f.periodo ?? <span className="muted">—</span>}</td>

                {f.vacia ? (
                  <td colSpan={5} className="muted">
                    Sin conciliación cargada. Hay que cargar el resumen y el mayor.
                  </td>
                ) : (
                  <>
                    <td className="num">{f.conciliados}/{f.consumos}</td>
                    <td className="num">
                      {f.nSinConciliar
                        ? <span className="tag bad">{f.nSinConciliar} · $ {pesos(f.sumaSinConciliar)}</span>
                        : <span className="tag ok">0</span>}
                    </td>
                    <td className="num">{f.sumaDiferencias ? `$ ${pesos(f.sumaDiferencias)}` : ''}</td>
                    <td>
                      {f.cuadra
                        ? <span className="tag ok">cuadra</span>
                        : <span className="tag bad" title={`Calculado $ ${pesos(f.totalCalculado)} · el resumen dice $ ${pesos(f.saldoResumen)}`}>
                            no cuadra
                          </span>}
                    </td>
                    <td>
                      <span className={`tag ${f.cerrada ? 'ok' : 'warn'}`}>
                        {f.cerrada ? 'CERRADA' : 'abierta'}
                      </span>
                      {f.cerradaPor && <div className="muted">{f.cerradaPor}</div>}
                    </td>
                  </>
                )}

                <td>{f.conciliacionId && <Link to={`/conciliacion/${f.conciliacionId}`}>abrir</Link>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
